import type { RecurrenceDefinition } from 'api/recurrence_definitions/endpoints/getRecurrenceDefinition';
import type { Recurrence } from 'api/recurrence_definitions/endpoints/types';
import {
  buildScheduleSnapshot,
  snapshotRecurrenceToRecurrence,
  type NodeInstanceScheduleSnapshot,
} from 'api/recurrence_definitions/operations/buildScheduleSnapshot';
import { isLegacyFlatSchedule, isNodeInstanceScheduleSnapshotValue } from 'api/recurrence_definitions/operations/submitScheduleTypes';
import { EditableRecurrenceDefinition } from 'pages/apps/platform/recurrence_definitions/edit/EditableRecurrenceDefinition';

// A manual ad-hoc submit schedule is a SINGLE recurrence rule (per PM: the
// submit page does not support multiple ad-hoc recurrences — multiple rules
// only exist on a saved recurrence definition). We model that one rule with the
// same `EditableRecurrence` the edit page uses, for UI consistency, held inside
// a throwaway `EditableRecurrenceDefinition` whose name/zone go unused — that
// reuses the model's wire⇄editable mapping, `toSave`, and `reinitialize`
// without a second code path. `manualRule(def)` is the single rule.

// Placeholder identity for the synthetic definition. Never reaches the wire:
// only `toSave().recurrences` is read for the schedule snapshot, never the
// name/zone. zone_name must be non-null or the model rejects it as orphaned.
const SYNTHETIC_ZONE = '__manual__';

const syntheticLoad = (recurrences: readonly Recurrence[], resubmitOnError = true): RecurrenceDefinition => ({
  recurrence_definition_id: 0,
  name: '',
  description: null,
  zone_name: SYNTHETIC_ZONE,
  resubmit_on_error: resubmitOnError,
  recurrences,
  // Synthetic placeholder: never reaches the wire and the manual flow has no
  // folder concept — only toSave().recurrences/resubmitOnError are read.
  folder_id: null,
  folder_path: null,
  created_at: '',
  updated_at: '',
});

/** A manual rule is "complete" enough to schedule: window_open needs a window, every other unit needs an interval. */
export const isCompleteRecurrence = (recurrence: Recurrence): boolean =>
  recurrence.unit === 'window_open' ? recurrence.scheduling_window != null : recurrence.interval != null;

/**
 * Create the editable holder for a manual submit schedule: one rule, started
 * incomplete (no interval) so an untouched schedule means "one-off submit, no
 * recurrence" until the user fills it in.
 */
export const createManualDefinition = (): EditableRecurrenceDefinition => {
  const definition = new EditableRecurrenceDefinition(null);
  definition.recurrences[0].interval = null;
  return definition;
};

/** The single editable rule of a manual submit schedule. */
export const manualRule = (definition: EditableRecurrenceDefinition) => definition.recurrences[0];

/** The manual rule as a wire recurrence, or `[]` when it isn't complete (a one-off). */
export const manualWireRecurrences = (definition: EditableRecurrenceDefinition): Recurrence[] => definition.toSave().recurrences.filter(isCompleteRecurrence);

/** Build the node-instance schedule snapshot for a manual submit, or `null` for a one-off. */
export const buildManualSnapshot = (definition: EditableRecurrenceDefinition): NodeInstanceScheduleSnapshot | null =>
  buildScheduleSnapshot(manualWireRecurrences(definition), definition.resubmitOnError);

/**
 * Seed the manual editor from a node instance's stored schedule (the clone /
 * re-submit flow). Takes the first rule only (single ad-hoc); an unrecognised
 * or empty schedule resets to a blank one-off rule.
 */
export const seedManualFromSchedule = (definition: EditableRecurrenceDefinition, schedule: unknown): void => {
  if (isNodeInstanceScheduleSnapshotValue(schedule)) {
    const recurrences = schedule.recurrences.map(snapshotRecurrenceToRecurrence);
    if (recurrences.length > 0) {
      definition.reinitialize(syntheticLoad([recurrences[0]], schedule.resubmit_on_error ?? true));
      return;
    }
  }
  if (isLegacyFlatSchedule(schedule)) {
    definition.reinitialize(syntheticLoad([schedule], schedule.resubmit_on_error ?? true));
    return;
  }
  definition.reinitialize(null);
  definition.recurrences[0].interval = null;
};
